import { useEffect, useState } from 'preact/hooks';
import type { SessionSnapshot } from '../session';
import { ChatPanel } from './ChatPanel';
import { DiagnosticsPanel } from './DiagnosticsPanel';
import { HostPreview } from './SessionMedia';
import { SessionHeader, formatRoomCode } from './SessionHeader';
import { ShareInvite } from './ShareInvite';

export interface HostViewProps {
  readonly snapshot: SessionSnapshot;
  readonly onEnd: () => void;
  readonly onLock: (locked: boolean) => void;
  readonly onRemoveViewer: () => void;
  readonly onRetry: () => void;
  readonly onToggleMicrophone: () => void;
}

export function HostView({
  snapshot,
  onEnd,
  onLock,
  onRemoveViewer,
  onRetry,
  onToggleMicrophone,
}: HostViewProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (copyState === 'idle') return;
    const timeout = window.setTimeout(() => setCopyState('idle'), 2200);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const copyInvite = async () => {
    if (!snapshot.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(snapshot.inviteUrl);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <main class="session-shell">
      <SessionHeader
        roomCode={snapshot.roomCode ?? ''}
        recovery={snapshot.recovery}
        expiresAt={snapshot.expiresAt}
        role="host"
        onLeave={onEnd}
        onRetry={onRetry}
      />

      <div class="session-layout">
        <section class="session-main" aria-label="Host controls">
          <div class="session-titlebar">
            <div>
              <p class="eyebrow">You are presenting</p>
              <h1>Your screen is live</h1>
            </div>
            <span class={`viewer-presence ${snapshot.viewerCount ? 'is-present' : ''}`}>
              <ViewerIcon />
              {snapshot.viewerCount === 1 ? '1 viewer' : 'Waiting for viewer'}
            </span>
          </div>

          <HostPreview stream={snapshot.localStream} />

          <div class="host-controls" aria-label="Sharing controls">
            <button
              class={`control-button ${snapshot.microphoneEnabled ? 'is-active' : ''}`}
              type="button"
              aria-pressed={snapshot.microphoneEnabled}
              disabled={snapshot.microphonePending}
              onClick={onToggleMicrophone}
            >
              <MicrophoneIcon active={snapshot.microphoneEnabled} />
              <span>
                {snapshot.microphonePending
                  ? 'Microphone…'
                  : snapshot.microphoneEnabled
                    ? 'Mic on'
                    : 'Mic off'}
              </span>
            </button>

            <div
              class={`control-indicator ${snapshot.sharedAudioState === 'available' ? 'is-active' : ''}`}
              role="status"
            >
              <AudioIcon />
              <span>{sharedAudioLabel(snapshot.sharedAudioState)}</span>
            </div>

            <button
              class={`control-button ${snapshot.locked ? 'is-active' : ''}`}
              type="button"
              aria-pressed={snapshot.locked}
              onClick={() => onLock(!snapshot.locked)}
            >
              <LockIcon locked={snapshot.locked} />
              <span>{snapshot.locked ? 'Locked' : 'Lock room'}</span>
            </button>

            <button
              class="control-button"
              type="button"
              disabled={snapshot.viewerCount === 0}
              onClick={onRemoveViewer}
            >
              <RemoveViewerIcon />
              <span>Remove viewer</span>
            </button>

            <button class="control-button control-button--danger" type="button" onClick={onEnd}>
              <StopIcon />
              <span>Stop sharing</span>
            </button>
          </div>

          <DiagnosticsPanel metrics={snapshot.metrics} iceState={snapshot.iceState} />
        </section>

        <aside class="session-sidebar" aria-label="Session details and chat">
          <section class="invite-card" aria-labelledby="invite-title">
            <div class="invite-card__header">
              <div>
                <p class="eyebrow">Invite someone</p>
                <h2 id="invite-title">Session access</h2>
              </div>
              <span class={`lock-badge ${snapshot.locked ? 'is-locked' : ''}`}>
                {snapshot.locked ? 'Locked' : 'Open'}
              </span>
            </div>
            <dl class="access-codes">
              <div>
                <dt>Room code</dt>
                <dd>{formatRoomCode(snapshot.roomCode ?? '')}</dd>
              </div>
              <div>
                <dt>PIN</dt>
                <dd>{snapshot.pin ?? '—'}</dd>
              </div>
            </dl>
            <button class="copy-button" type="button" onClick={() => void copyInvite()}>
              <CopyIcon />
              {copyState === 'copied'
                ? 'Invite link copied'
                : copyState === 'failed'
                  ? 'Copy failed — retry'
                  : 'Copy invite link'}
            </button>
            {snapshot.inviteUrl && snapshot.roomCode && snapshot.pin ? (
              <ShareInvite
                inviteUrl={snapshot.inviteUrl}
                roomCode={snapshot.roomCode}
                pin={snapshot.pin}
              />
            ) : null}
            <p class="invite-card__note">
              The link includes a private one-time session credential. Share it only with your
              viewer.
            </p>
            <span class="sr-only" role="status" aria-live="polite">
              {copyState === 'copied'
                ? 'Invite link copied to clipboard.'
                : copyState === 'failed'
                  ? 'Could not copy the invite link. Try again or use a share option.'
                  : ''}
            </span>
          </section>
          {snapshot.chat ? <ChatPanel chat={snapshot.chat} /> : null}
        </aside>
      </div>
    </main>
  );
}

function sharedAudioLabel(state: SessionSnapshot['sharedAudioState']): string {
  switch (state) {
    case 'available':
      return 'Screen audio on';
    case 'unavailable':
      return 'No screen audio';
    case 'not-requested':
      return 'No screen audio';
  }
}

function ViewerIcon() {
  return <Icon path="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0" />;
}

function MicrophoneIcon({ active }: { readonly active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6" />
      {!active ? <path class="icon-strike" d="M4 4l16 16" /> : null}
    </svg>
  );
}

function AudioIcon() {
  return <Icon path="M5 10v4h3l4 3V7l-4 3H5Zm11-1a4 4 0 0 1 0 6" />;
}

function LockIcon({ locked }: { readonly locked: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d={locked ? 'M8 10V7a4 4 0 0 1 8 0v3' : 'M16 10V7a4 4 0 0 0-7.5-2'} />
    </svg>
  );
}

function RemoveViewerIcon() {
  return (
    <Icon path="M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-6 9a6 6 0 0 1 10.8-3.6M16 12l5 5m0-5-5 5" />
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="5" width="14" height="14" rx="3" />
      <rect class="icon-fill" x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <Icon path="M8 8h10a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Zm8 0V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1" />
  );
}

function Icon({ path }: { readonly path: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}
